import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { toast } from "react-toastify";

import CommonModal from "../component/common/commonModal";
import { layoutCSS, cellSX } from "../component/common/commonStyle";

import {
  useAddCategoryMutation,
  useGetCategoriesQuery,
  useUpdateCategoryMutation,
  useDeleteCategoryMutation,
} from "../store/services/categoryApi";

const ManageCategory = () => {
  const resetForm = () => {
    setCategoryName("");
    setErrors({});
    setSelected(null);
    setIsEditing(false);
  };
  const { data, isLoading } = useGetCategoriesQuery();

  const [addCategory] = useAddCategoryMutation();
  const [updateCategory] = useUpdateCategoryMutation();
  const [deleteCategory] = useDeleteCategoryMutation();

  const [openForm, setOpenForm] = useState(false);
  const [openModal, setOpenModal] = useState(false);

  const [isEditing, setIsEditing] = useState(false);
  const [selected, setSelected] = useState(null);

  const [categoryName, setCategoryName] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);


  const validate = () => {
    const err = {};
    if (!categoryName.trim()) {
      err.categoryName = "Category name is required";
    }
    setErrors(err);
    return Object.keys(err).length === 0;
  };


  const handleAdd = async () => {
    if (!validate()) return;

    setLoading(true);
    try {
      const res = await addCategory({
        category_name: categoryName,
      }).unwrap();

      toast.success(res.message || "Category added");
      resetForm();
      setOpenForm(false);
      setCategoryName("");
    } catch (err) {
      toast.error(err?.data?.message || "Error");
    } finally {
      setLoading(false);
    }
  };


  const handleEdit = (cat) => {
    setSelected(cat);
    setCategoryName(cat.category_name);
    setIsEditing(true);
    setOpenModal(true);
  };


  const handleDelete = (cat) => {
    setSelected(cat);
    setIsEditing(false);
    setOpenModal(true);
  };


  const handleUpdate = async () => {
    if (!validate()) return;

    setLoading(true);
    try {
      const res = await updateCategory({
        category_id: selected.category_id,
        category_name: categoryName,
      }).unwrap();

      toast.success(res.message || "Updated");
      resetForm();
      setOpenModal(false);
    } catch (err) {
      toast.error(err?.data?.message || "Error");
    } finally {
      setLoading(false);
    }
  };


  const handleConfirmDelete = async () => {
    try {
      const res = await deleteCategory({
        category_id: selected.category_id,
      }).unwrap();

      toast.success(res.message || "Deleted");
      setOpenModal(false);
    } catch (err) {
      toast.error(err?.data?.message || "Error");
    }
  };

  return (
    <Box sx={layoutCSS}>
      {/* Header */}
      <Typography
        variant="h6"
        sx={{
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontSize: "0.9rem",
          fontWeight: "bold",
        }}
      >
        <span>Manage Category</span>

        <Button
          variant="contained"
          onClick={() => {
            resetForm();
            setOpenForm(true);
          }}
        >
          New Category
        </Button>
      </Typography>

      {/* Add Modal */}
      <CommonModal open={openForm} setOpen={setOpenForm}>
        <Box>
          {errors.categoryName && (
            <Typography color="error" fontSize="0.7rem">
              {errors.categoryName}
            </Typography>
          )}

          <TextField
            label="Category Name"
            fullWidth
            size="small"
            value={categoryName}
            onChange={(e) => setCategoryName(e.target.value)}
            sx={{ mb: 2 }}
          />

          <Box display="flex" gap={1}>
            <Button
              variant="contained"
              fullWidth
              onClick={handleAdd}
              disabled={loading}
              sx={{ fontSize: "0.7rem" }}
            >
              Submit
            </Button>

            <Button
              variant="outlined"
              color="error"
              fullWidth
              onClick={() => setOpenForm(false)}
              sx={{ fontSize: "0.7rem" }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </CommonModal>

      {/* Table */}
      <TableContainer
        component={Paper}
        sx={{ borderRadius: 0.5, border: "1px solid #1b1b1bff" }}
      >
        <Table>
          <TableHead
            sx={{
              bgcolor: "#0288d1",
              "& th": {
                fontWeight: "bold",
                color: "white",
                fontSize: "0.75rem",
                padding: "8px",
                borderRight: "1px solid #1b1b1bff",
                borderBottom: "1px solid #1b1b1bff",
              },
            }}
          >
            <TableRow>
              <TableCell>Serial.No</TableCell>
              <TableCell>Category Name</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {data?.data?.map((cat, index) => (
              <TableRow key={cat.category_id}>
                <TableCell sx={cellSX}>{index + 1}</TableCell>
                <TableCell sx={cellSX}>{cat.category_name}</TableCell>

                <TableCell sx={cellSX}>
                  <EditIcon
                    onClick={() => handleEdit(cat)}
                    sx={{ cursor: "pointer", mr: 2 }}
                  />

                  <DeleteIcon
                    onClick={() => handleDelete(cat)}
                    sx={{ cursor: "pointer" }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit/Delete Modal */}
      <CommonModal open={openModal} setOpen={setOpenModal}>
        {isEditing ? (
          <Box>
            <TextField
              label="Category Name"
              fullWidth
              size="small"
              value={categoryName}
              onChange={(e) => setCategoryName(e.target.value)}
              sx={{ mb: 2 }}
            />

            <Box display="flex" gap={1}>
              <Button variant="contained" fullWidth onClick={handleUpdate}>
                Save
              </Button>

              <Button
                variant="outlined"
                color="error"
                fullWidth
                onClick={() => setOpenModal(false)}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        ) : (
          <Box>
            <Typography mb={2}>
              Are you sure you want to delete this category?
            </Typography>

            <Box display="flex" gap={1}>
              <Button
                variant="contained"
                color="error"
                fullWidth
                onClick={handleConfirmDelete}
              >
                Yes
              </Button>

              <Button
                variant="outlined"
                fullWidth
                onClick={() => setOpenModal(false)}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        )}
      </CommonModal>
    </Box>
  );
};

export default ManageCategory;
